/**
 * Notification API Service
 * Handles device token registration and notification preferences with the server
 */

import apiClient from './axios'
import { logger } from '@/services/logger'
import type { DeviceTokenPayload, NotificationPreferences } from '@/types/notifications'

/**
 * Register device token with the server
 * Server will use this token to send push notifications
 */
export async function registerDeviceToken(payload: DeviceTokenPayload): Promise<void> {
    try {
        logger.info('[NotificationAPI] Registering device', {
            workerId: payload.workerId,
            platform: payload.platform,
            hasToken: !!payload.token,
            tokenPreview: payload.token ? payload.token.substring(0, 20) + '...' : 'N/A (web)'
        })

        await apiClient.post('/api/warden/notifications/register-token', payload)

        logger.info('[NotificationAPI] Device registered successfully')
    } catch (error) {
        logger.error('[NotificationAPI] Failed to register device token:', error as Error)
        throw error
    }
}

/**
 * Unregister device token from the server
 * Call this when user logs out or removes device
 */
export async function unregisterDeviceToken(workerId: number, token: string): Promise<void> {
    try {
        logger.info('[NotificationAPI] Unregistering device token', {
            workerId,
            tokenPreview: token.substring(0, 20) + '...'
        })

        await apiClient.post('/api/warden/notifications/unregister-token', {
            workerId,
            token
        })

        logger.info('[NotificationAPI] Device token unregistered successfully')
    } catch (error) {
        logger.error('[NotificationAPI] Failed to unregister device token:', error as Error)
        // Don't throw - this is not critical
    }
}

/**
 * Update notification preferences on the server
 * Server will use these preferences to schedule push notifications
 */
export async function updateNotificationPreferences(
    workerId: number,
    preferences: NotificationPreferences
): Promise<void> {
    try {
        logger.info('[NotificationAPI] Updating notification preferences', {
            workerId,
            preferences
        })

        await apiClient.put('/api/warden/notifications/preferences', {
            workerId,
            preferences
        })

        logger.info('[NotificationAPI] Notification preferences updated successfully')
    } catch (error) {
        logger.error('[NotificationAPI] Failed to update notification preferences:', error as Error)
        throw error
    }
}

/**
 * Get notification preferences from the server
 */
export async function getNotificationPreferences(workerId: number): Promise<NotificationPreferences> {
    try {
        logger.info('[NotificationAPI] Fetching notification preferences', { workerId })

        const response = await apiClient.get<NotificationPreferences>(
            `/api/warden/notifications/preferences/${workerId}`
        )

        logger.info('[NotificationAPI] Notification preferences fetched successfully')

        return response.data
    } catch (error) {
        logger.error('[NotificationAPI] Failed to fetch notification preferences:', error as Error)
        throw error
    }
}

/**
 * Test push notification (for admin/testing purposes)
 * Server will send a test push notification to this device
 */
export async function sendTestNotification(workerId: number, fcmToken?: string): Promise<void> {
    try {
        const payload: { workerId: number; fcmToken?: string } = { workerId }
        if (fcmToken) {
            payload.fcmToken = fcmToken
        }

        logger.info('[NotificationAPI] Requesting test notification', {
            workerId,
            hasFcmToken: !!fcmToken,
            fcmTokenPreview: fcmToken ? fcmToken.substring(0, 20) + '...' : 'N/A'
        })
        console.log('[NotificationAPI] Sending test notification request:', {
            url: '/api/warden/notifications/test',
            workerId,
            payload
        })

        const response = await apiClient.post('/api/warden/notifications/test', payload)

        logger.info('[NotificationAPI] Test notification requested successfully', {
            status: response.status,
            data: response.data
        })
        console.log('[NotificationAPI] Response:', response)
    } catch (error) {
        logger.error('[NotificationAPI] Failed to send test notification:', error as Error, {
            workerId,
            errorDetails: error
        })
        console.error('[NotificationAPI] Error sending test notification:', error)
        throw error
    }
}

